/**
 * Copyright 2022 (C) Blink - All Rights Reserved
 *
 * This source code is protected under international copyright law.  All rights
 * reserved and protected by the copyright holders.
 * All files are confidential and only available to authorized individuals with the
 * permission of the copyright holders. If you encounter any file and do not have
 * permission, please get in touch with the copyright holders and delete this file.
 */
import { useEffect, useMemo, useRef, useState } from "react";

import cn from "classnames";
import toast from "react-hot-toast";
import useEventRouting from "hooks/useEventRouting";

import LoadingIcon from "assets/Icons/LoadingIcon";
import { useTranslation } from "react-i18next";
import LOCAL_STORAGE_KEYS from "constants/STORAGE_KEYS";
import { getUserId } from "Models/User";
import useCompanion from "hooks/useCompanion";
import { GetTripsResponse } from "lib/tripsApiTypes";
import { GetProfileResponse } from "lib/userApiTypes";
import { getApplicationStatusText } from "Models/BlinkApplication";
import STATES from "Models/UIStates";
import { isUserBookingHisFlight, isVmsEnabled } from "Models/User";
import ROUTES from "routes";
import { useInvitation } from "state/InvitationContext";
import { useOrgData } from "state/OrgDataContext";
import { useUI } from "state/UIContext";
import { useUser } from "state/UserContext";
import applySuffix from "utils/applySuffix";

import {
  Button,
  Heading,
  HR,
  CompanionCard,
  LoadingDots,
  Stack,
  Text,
  FontAwesomeIcon,
  QRModal,
} from "components";
import { PageLayout } from "templates";

import ActivitiesDetails from "./ActivitiesDetails";
// TEMPORARY (BG-22291): download-app QR section hidden everywhere. Restore import + <QRSection /> below to re-enable.
// import QRSection from "./QRSection";
import RequireAttention from "./RequireAttention";
import TripDetails from "./TripDetails";
import {
  FlightSection,
  MatchSection,
  StaySection,
} from "./TripDetails/Components";
import { useAuth } from "state/AuthContext";

import { pushToDataLayer } from "utils/gtm";

/** Orders user trips if he is booking his own flights */
const orderTrips = (
  trips: GetTripsResponse["approved_trips"],
  userData: GetProfileResponse | null,
) => {
  if (!isUserBookingHisFlight(userData)) return trips;
  return trips?.sort((a, b) => {
    if (a.flights?.length === 0) return -1;
    if (b.flights?.length === 0) return 1;
    return 0;
  });
};

const requiresAttention = (
  tripsData: GetTripsResponse["approved_trips"] = [],
) => {
  const willAddFlight = tripsData.some(
    (trip) => trip.will_add_flight && !trip.flights?.length,
  );

  const willAddReservation = tripsData.some(
    (trip) => trip.will_add_reservation && !trip.reservations?.length,
  );

  return willAddFlight || willAddReservation;
};

const Dashboard = () => {
  const { t } = useTranslation();
  const {
    userData,
    setUserData,
    getUserProfile,
    isFormCompleted,
    dashboardData,
    isLoadingDashboardData,
  } = useUser();
  const [, setCompanion] = useCompanion();
  const { navigateWithEvent } = useEventRouting();
  const { isLoggedIn } = useAuth();
  const { openModal } = useUI();
  const { dataContent: EVENT_CONTENT } = useOrgData();

  const { attendees, isLoadingAttendees } = useInvitation({
    fetchAttendees: isLoggedIn,
    hardFetch: true,
  });

  const userBlinkId = userData?.application?.blink_id;
  // event_is_vms_enabled is only served by the attendees endpoint, not the
  // profile one that fills userData — attendees[0] is the main attendee.
  const hasUserQRCode =
    !isVmsEnabled(attendees[0]) &&
    Boolean(EVENT_CONTENT.userIdQRCode) &&
    Boolean(userBlinkId);

  // Some login flows reach the dashboard before the full profile was fetched
  // with an eventId, leaving userData without its application (blink_id).
  // Hydrate it quietly once — getUserProfile has no global loading/login side
  // effects, unlike AuthContext.fetchUserData.
  const hasHydratedProfile = useRef(false);
  useEffect(() => {
    if (!isLoggedIn || !userData || userBlinkId || hasHydratedProfile.current) {
      return;
    }
    const id =
      getUserId(userData) || localStorage.getItem(LOCAL_STORAGE_KEYS.id);
    if (!id) return;

    hasHydratedProfile.current = true;
    getUserProfile(id, "user", true)
      .then((profile: any) => {
        if (profile) {
          setUserData((prev) => ({ ...prev, ...profile }));
        }
      })
      .catch(() => {});
  }, [isLoggedIn, Boolean(userData), userBlinkId]);

  const handleViewUserQRCode = () => {
    pushToDataLayer({
      event: "view_user_qr_code",
      screen_name: "dashboard",
      journey_step: "dashboard",
    });

    openModal({
      content: (
        <QRModal
          qrUrl={`${EVENT_CONTENT.userIdQRCode},${userBlinkId}`}
          title={t("guestDetails.formFields.userIdQRCodeModalTitle")}
        />
      ),
    });
  };

  const requireAttention = useMemo(() => {
    return requiresAttention([
      ...(dashboardData.approved_trips ?? []),
      ...(dashboardData.pending_trips ?? []),
    ]);
  }, [dashboardData.approved_trips, dashboardData.pending_trips, userData]);

  const hasEventSchedule = Boolean(
    Object.keys(dashboardData.activities ?? {}).length ||
      dashboardData.approved_trips?.length ||
      dashboardData.general_flights?.length ||
      dashboardData.general_reservations?.length ||
      dashboardData.general_sessions?.length ||
      dashboardData.pending_trips?.length,
  );

  const [isFlightsExpanded, setFlightsExpanded] = useState(false);
  const [isReservationsExpanded, setReservationsExpanded] = useState(false);
  const [isSessionsExpanded, setSessionsExpanded] = useState(false);
  const [isActivitiesExpanded, setActivitiesExpanded] = useState(false);

  useEffect(() => {
    if (isLoadingAttendees)
      toast.loading(t("common.labels.loading"), { duration: 1000 });
  }, [isLoadingAttendees]);

  const handleSelectCompanion = (companion: any) => {
    setCompanion(companion);

    if (companion?.is_personal_form_completed) {
      navigateWithEvent(
        ROUTES.dashboard.guest(companion?.hash_id || companion?.id),
      );
    } else {
      if (
        companion?.role === "companion" ||
        companion?.id === "new" ||
        !companion?.id
      ) {
        navigateWithEvent(ROUTES.onboard.personalInfo(companion?.id || "new"));
      } else {
        navigateWithEvent(ROUTES.onboard.personalInfo(companion?.hash_id));
      }
    }
  };

  // if the form is not completed navigate back to the onBoarding
  useEffect(() => {
    if (!isFormCompleted) {
      navigateWithEvent(ROUTES.onboard.registration);
    }
  }, [isFormCompleted]);

  return (
    <PageLayout
      title={t("dashboard.welcomeMsg", {
        name: `${userData?.first_name ?? ""} ${userData?.last_name ?? ""}`,
      })}
      hasGoBack={false}
      containerClassName="md:!px-0 !max-w-[900px]"
      headingClassName="px-6 md:px-0"
    >
      <Stack className="gap-y-8 md:gap-y-14 -text--theme-colors-text-body mt-8 md:mt-12">
        {(dashboardData.can_add_flight ||
          dashboardData.can_add_reservation) && (
          <>
            <Stack
              className="-bg--theme-colors-surface-neutral-level04 border -border--theme-colors-warning-default -rounded-outer-container gap-y-10 px-14 py-10"
              id="trips"
            >
              <div className="flex items-center gap-x-5 -text--theme-colors-warning-default">
                <LoadingIcon />
                <Text className="text-xl">{t("requireAttention.title")}</Text>
              </div>
              <RequireAttention
                canAddFlight={!!dashboardData.can_add_flight}
                canAddAccommodation={!!dashboardData.can_add_reservation}
              />
            </Stack>

            <HR className="hidden md:block !my-0" />
          </>
        )}

        <div className="px-6 md:px-0">
          {isLoadingDashboardData && (
            <section className="my-10 flex justify-center">
              <LoadingDots />
            </section>
          )}

          <Stack className="gap-y-6">
            <div className="flex flex-col w-full">
              <Heading
                as="h3"
                className="-text--theme-colors-text-title-brand text-2xl"
              >
                {t("dashboard.usersType")}
              </Heading>

              <Text className="-text--theme-colors-text-body">
                {t("dashboard.usersInstructions")}
              </Text>
            </div>

            {!isLoadingAttendees ? (
              <div
                className={cn(
                  "flex flex-wrap justify-center gap-x-8 lg:gap-x-16 lg:justify-start gap-y-16",
                )}
              >
                {attendees?.map((companion, i) => {
                  const infoState = getApplicationStatusText(companion);
                  let state;

                  if (STATES[infoState] === STATES.success) {
                    state = "completed" as const;
                  } else if (STATES[infoState] === STATES.failed) {
                    state = "denied" as const;
                  } else {
                    state = "pending" as const;
                  }

                  if (companion?.is_personal_form_completed === false) {
                    state = "initial" as const;
                  }

                  const companionDetails = { ...companion };
                  if (!companionDetails.hasOwnProperty("id")) {
                    const companionNumber = i;
                    companionDetails.first_name = applySuffix(companionNumber);
                    companionDetails.last_name = "Companion";
                  }

                  return (
                    <CompanionCard
                      key={i}
                      variant="dashboard"
                      companion={companionDetails}
                      handleSelectCompanion={() =>
                        handleSelectCompanion(companion)
                      }
                      state={state}
                      onViewQRCode={
                        hasUserQRCode &&
                        companion?.role !== "companion" &&
                        companion?.id
                          ? handleViewUserQRCode
                          : undefined
                      }
                    />
                  );
                })}
              </div>
            ) : (
              <section className="flex justify-center mt-5">
                <LoadingDots />
              </section>
            )}
          </Stack>
        </div>


        {hasEventSchedule && (
          <HR className="hidden sm:block !my-0 -border--theme-colors-border-neutral-09" />
        )}

        <Stack className="gap-y-2.5 md:gap-y-5">
          {hasEventSchedule && (
            <div className="flex flex-col w-ful px-6 md:px-0 mb-5 md:mb-0">
              <Heading
                as="h3"
                className="-text--theme-colors-text-title-brand text-2xl"
              >
                {t("dashboard.eventSchedule")}
              </Heading>

              <Text className="-text--theme-colors-text-body">
                {t("dashboard.eventScheduleSubtitle")}
              </Text>
            </div>
          )}

          {!!dashboardData.general_flights?.length && (
            <ExpandSection
              header={t("dashboard.myFlightsLabel")}
              isExpanded={isFlightsExpanded}
              handleExpand={setFlightsExpanded}
            >
              <Stack className="gap-y-5 md:gap-y-10">
                {dashboardData.general_flights.map((flight, i) => (
                  <FlightSection key={i} flight={flight} number={i + 1} />
                ))}
              </Stack>
            </ExpandSection>
          )}

          {!!dashboardData.general_reservations?.length && (
            <ExpandSection
              header={t("dashboard.myReservationsLabel")}
              isExpanded={isReservationsExpanded}
              handleExpand={setReservationsExpanded}
              trackingSuffix="reservations"
            >
              <StaySection
                reservations={dashboardData.general_reservations}
                userId={userData?.hash_id!}
              />
            </ExpandSection>
          )}

          {Object.keys(dashboardData.general_sessions ?? {}).length > 0 && (
            <ExpandSection
              header={t("dashboard.mySessionsLabel")}
              isExpanded={isSessionsExpanded}
              handleExpand={setSessionsExpanded}
            >
              <MatchSection
                sessions={dashboardData.general_sessions}
                blinkId={userData?.application?.blink_id!}
              />
            </ExpandSection>
          )}

          {Object.keys(dashboardData.activities ?? {}).length > 0 && (
            <ExpandSection
              header={t("dashboard.myActivitiesLabel")}
              isExpanded={isActivitiesExpanded}
              handleExpand={setActivitiesExpanded}
              trackingSuffix="activities"
            >
              <ActivitiesDetails activities={dashboardData.activities} />
            </ExpandSection>
          )}

          {!!dashboardData.approved_trips?.length && (
            <Stack className="items-center gap-y-5">
              {orderTrips(dashboardData.approved_trips, userData)?.map(
                (trip, i) => {
                  return (
                    <TripDetails
                      key={trip.id}
                      trip={trip}
                      type="approved"
                      number={i + 1}
                    />
                  );
                },
              )}
            </Stack>
          )}

          {!!dashboardData.pending_trips?.length && (
            <Stack className="items-center gap-y-5">
              {orderTrips(dashboardData.pending_trips, userData)?.map(
                (trip, i) => {
                  return (
                    <TripDetails
                      key={trip.id}
                      trip={trip}
                      type="pending"
                      number={
                        (dashboardData.approved_trips?.length ?? 0) + i + 1
                      }
                    />
                  );
                },
              )}
            </Stack>
          )}
        </Stack>

        {/* TEMPORARY (BG-22291): download-app QR section hidden for all orgs/events. Uncomment (and the import above) to re-enable. */}
        {/* <QRSection /> */}
        
      </Stack>
    </PageLayout>
  );
};
const ExpandSection = ({
  header,
  isExpanded,
  handleExpand,
  children,
  trackingSuffix,
}: {
  header: string;
  isExpanded: boolean;
  handleExpand: (preState: boolean) => void;
  children: React.ReactNode;
  trackingSuffix?: string;
}) => {
  const { t } = useTranslation();
  return (
    <Stack className="w-full -bg--theme-colors-surface-neutral-level02 border -border--theme-colors-border-neutral-11 -rounded-outer-container px-6 py-8 md:px-14">
      <Heading
        as="h2"
        className="-text--theme-colors-text-title-brand text-2xl"
      >
        {header}
      </Heading>

      {isExpanded && <Stack className="mt-10">{children}</Stack>}

      <div className="flex w-full mt-2.5 gap-x-12">
        <HR className="my-5 -border--theme-colors-action-primary-default-bg h-0" />

        <Button
          variant={"ghost-unstyled"}
          onClick={() => {
            if (trackingSuffix) {
              const eventName = isExpanded
                ? `my_${trackingSuffix}_collapse`
                : `my_${trackingSuffix}_expand`;
              pushToDataLayer({
                event: eventName,
                screen_name: "dashboard",
                action: isExpanded ? "collapse" : "expand",
              });
            }
            handleExpand(!isExpanded);
          }}
          className="text-sm"
        >
          <div className="flex gap-x-2">
            <Text>
              {isExpanded
                ? t("common.buttons.collapse")
                : t("common.buttons.expand")}
            </Text>

            <div>
              <FontAwesomeIcon
                name="fa-chevron-down"
                className={cn(!isExpanded ? "" : "rotate-180")}
              />
            </div>
          </div>
        </Button>

        <HR className="my-5 -border--theme-colors-action-primary-default-bg h-0" />
      </div>
    </Stack>
  );
};

export default Dashboard;
